/**
* @file 文件导航
* @author Ice(chenzhouji@baidu.com)
*/
import _ from 'lodash';
import React, {FunctionComponent, useState, useEffect, useRef, useCallback} from 'react';
import {Link, navigate} from 'gatsby';
import classnames from 'classnames';

const toggleExpandedItem = (pool: string[], item: string) => {
    const isExpanded = _.includes(pool, item);
    return isExpanded
        ? _.filter(pool, i => i !== item)
        : _.concat(pool, item);
};

type SecondaryNavProps = {
    items: TreeNestedItem[]
    name: string
    pageContext: any
    slugName: string,
    computeBranchNodeOffset?: (depth: number) => number,
    computeLeafNodeOffset?: (depth: number) => number
}

const getParents = (elm: HTMLElement) => {
    let element: any = elm;
    const parents = [];

    while(element.parentNode && element.parentNode.nodeName.toLowerCase() != 'body') {
        element = element.parentNode;
        if (_.includes(element.className.split(' '), 'submenu')) {
            parents.push(element.dataset.key);
        }
    }
    return parents;
};

const getIdBySlugInTree = (tree: any, slug: string) => {
    let id;
    _.forEach(tree, item => {
        if (item.filePath && `/${item.filePath.replace('.md', '')}/` === slug) {
            id = item.id;
        }
        if (item.children && item.children.length) {
            const result = getIdBySlugInTree(item.children, slug);
            if (result) {
                id = result;
            }
        }
    });
    return id;
}

const SecondaryNav: FunctionComponent<SecondaryNavProps> = props => {
    const {items, slugName, pageContext, computeBranchNodeOffset, computeLeafNodeOffset} = props;

    let isMount = useRef(false);
    let prevActiveItem = useRef(null);
    let prevExpandedItemsProp: any = useRef(null);
    let prevExpandedItems: any = useRef(null);

    const expandedItemsProp = _.map(pageContext.parents || [], p => p.id);

    const activeItemProp = slugName === 'uuid'
        ? pageContext.slug && pageContext.slug.replace(/\//g, '')
        : getIdBySlugInTree(items, pageContext.slug);

    const [activeItem, setActiveItem] : [any, React.Dispatch<any>] = useState(activeItemProp);
    const [expandedItems, setExpandedItems] : [any, React.Dispatch<any>] = useState(expandedItemsProp);

    const clickSubMenu = useCallback(e => {
        e.persist();
        setExpandedItems((items: any) => toggleExpandedItem(items, e.target.dataset.key));
        // const firstLink =  _.get(e, 'target.nextSibling.firstChild.dataset.key');
        // firstLink && navigate(firstLink);
    }, [setExpandedItems]);

    const clickMenuItem = useCallback(e => {
        e.preventDefault();

        const k = e.target.dataset.key;
        setActiveItem(k);

        const parents = getParents(e.target);
        setExpandedItems([...parents, e.target.dataset.key]);
    }, [setActiveItem]);

    const updateScroll = useCallback(() => {
        const navElm: any = document.querySelector('.secondary-nav') || {clientHeight: 0, scrollTop: 0};
        const linkElm: any = document.querySelector(`li[data-key="${activeItemProp}"]`) || {offsetTop: 0};
        const navHeadElm = document.querySelector('.sider .head') || {clientHeight: 0};

        // 当link不在可视区域内，scroll一下
        const needScroll = linkElm.offsetTop > navElm.scrollTop + navElm.clientHeight
            || linkElm.offsetTop + navHeadElm.clientHeight <= navElm.scrollTop;
        if (navElm.scrollTo && needScroll) {
            navElm.scrollTo(0, linkElm.offsetTop - 90);
        }
    }, [activeItemProp, prevActiveItem]);

    const updateActiveItem = useCallback(() => {
        if (activeItemProp !== prevActiveItem.current) {
            setActiveItem(activeItemProp);
            updateScroll();
            prevActiveItem.current = activeItemProp;
        }
    }, [activeItemProp, prevActiveItem, setActiveItem, updateScroll]);

    const updateExpandedItems = useCallback(() => {
        if (!_.isEqual(prevExpandedItemsProp.current, expandedItemsProp)) {
            setExpandedItems(expandedItemsProp);
            prevExpandedItemsProp.current = expandedItemsProp;
        }
    }, [expandedItemsProp, prevExpandedItemsProp, setExpandedItems]);

    useEffect(() => {
        updateActiveItem();
        updateExpandedItems();
        if (!isMount.current) {
            updateScroll();

            isMount.current = true;
        }
    }, [updateActiveItem, updateExpandedItems, prevExpandedItems, expandedItems]);

    const renderMenuItems = (items: TreeNestedItem[], depth = 0) => items.map(item => {
        if (item.disabled) {
            return null;
        }

        const key = item.id;

        if (item.children && item.children.length > 0) {
            const isExpanded = _.includes(expandedItems, key);
            const display = !!isExpanded ? 'block' : 'none';
            const menuClassName = classnames({
                [`level-${depth}`]: true,
                'submenu': true,
                'submenu--expanded': isExpanded
            });
            const paddingLeft = computeBranchNodeOffset ? computeBranchNodeOffset(depth) : 20 * (depth + 1);

            return (
                <li className={menuClassName} key={key} data-key={key} title={item.name}>
                    <span
                        className="submenu__title"
                        data-key={key}
                        onClick={clickSubMenu}
                        style={{paddingLeft}}
                    >{item.name}</span>
                    <ul className="submenu__items" style={{display}}>{renderMenuItems(item.children, depth + 1)}</ul>
                </li>
            );
        }

        const liClassName =classnames({
            [`level-${depth}`]: true,
            'menu-item': true,
            'menu-item--active': activeItem === key || activeItemProp === key,
        });

        const to = slugName === 'uuid' ? item.path : item.filePath && item.filePath.replace('.md', '');
        const paddingLeft = computeLeafNodeOffset ? computeLeafNodeOffset(depth) : 20 * (depth);

        return (
            <li
                className={liClassName}
                key={key}
                data-key={key}
                onClick={clickMenuItem}
            >
                <Link
                    data-key={key}
                    data-filepath={item.filePath}
                    style={{paddingLeft}}
                    to={to}>{item.name}
                </Link>
            </li>
        );
    });

    return (
        <ul className="secondary-nav menu">
            {renderMenuItems(items || [])}
        </ul>
    );
};

export default SecondaryNav;

